import React from 'react';
import { useTheme } from './ThemeContext';

const LiquidGlassIconButton = ({
  onClick,
  disabled = false,
  variant = 'ghost',
  size = 'medium',
  className = '',
  title,
  icon: Icon,
  badge,
  badgeColor = 'red',
  isActive = false,
  children,
  ...props
}) => {
  const { isDarkMode } = useTheme();

  // Variant styles for icon buttons - optimiert für Light/Dark Mode
  const variants = {
    primary: {
      hoverBg: isDarkMode ? 'bg-blue-500/10' : 'bg-blue-500/15',
      hoverIconColor: isDarkMode ? 'group-hover/iconbtn:text-blue-300' : 'group-hover/iconbtn:text-blue-500'
    },
    secondary: {
      hoverBg: isDarkMode ? 'bg-gray-500/10' : 'bg-gray-500/15',
      hoverIconColor: isDarkMode ? 'group-hover/iconbtn:text-gray-300' : 'group-hover/iconbtn:text-gray-700'
    },
    success: {
      hoverBg: isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-500/15',
      hoverIconColor: isDarkMode ? 'group-hover/iconbtn:text-emerald-300' : 'group-hover/iconbtn:text-emerald-600'
    },
    danger: {
      hoverBg: isDarkMode ? 'bg-red-500/10' : 'bg-red-500/15',
      hoverIconColor: isDarkMode ? 'group-hover/iconbtn:text-red-300' : 'group-hover/iconbtn:text-red-600'
    },
    warning: {
      hoverBg: isDarkMode ? 'bg-amber-500/10' : 'bg-amber-500/15',
      hoverIconColor: isDarkMode ? 'group-hover/iconbtn:text-amber-300' : 'group-hover/iconbtn:text-amber-600'
    },
    purple: {
      hoverBg: isDarkMode ? 'bg-purple-500/10' : 'bg-purple-500/15',
      hoverIconColor: isDarkMode ? 'group-hover/iconbtn:text-purple-300' : 'group-hover/iconbtn:text-purple-600'
    },
    orange: {
      hoverBg: isDarkMode ? 'bg-orange-500/10' : 'bg-orange-500/15',
      hoverIconColor: isDarkMode ? 'group-hover/iconbtn:text-orange-300' : 'group-hover/iconbtn:text-orange-600'
    },
    ghost: {
      hoverBg: isDarkMode ? 'bg-blue-500/10' : 'bg-blue-500/15',
      hoverIconColor: isDarkMode ? 'group-hover/iconbtn:text-blue-300' : 'group-hover/iconbtn:text-blue-600'
    }
  };

  // Size styles
  const sizes = {
    small: {
      padding: 'p-1.5',
      iconSize: 'w-4 h-4'
    },
    medium: {
      padding: 'p-2.5',
      iconSize: 'w-5 h-5'
    },
    large: {
      padding: 'p-3',
      iconSize: 'w-6 h-6'
    }
  };

  // Badge colors
  const badgeColors = {
    red: 'from-red-500/80 to-red-600/80 shadow-[0_4px_16px_rgba(239,68,68,0.4)]',
    orange: 'from-orange-500/80 to-orange-600/80 shadow-[0_4px_16px_rgba(245,158,11,0.4)]',
    blue: 'from-blue-500/80 to-blue-600/80 shadow-[0_4px_16px_rgba(59,130,246,0.4)]',
    green: 'from-green-500/80 to-green-600/80 shadow-[0_4px_16px_rgba(34,197,94,0.4)]',
    purple: 'from-purple-500/80 to-purple-600/80 shadow-[0_4px_16px_rgba(147,51,234,0.4)]'
  };

  const variantStyle = variants[variant];
  const sizeStyle = sizes[size];

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`
        relative group/iconbtn rounded-2xl transition-all duration-300
        transform ${isDarkMode ? 'hover:scale-[1.03]' : 'hover:scale-[1.05]'} disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.96]
        ${isActive ? (isDarkMode ? 'ring-2 ring-orange-400/50' : 'ring-2 ring-orange-400/60') : ''}
        ${sizeStyle.padding} ${className}
      `}
      style={{
        background: 'transparent'
      }}
      {...props}
    >
      {/* Enhanced Glass Background - Optimiert für Light Mode */}
      <div
        className={`absolute inset-0 rounded-2xl transition-all duration-300 pointer-events-none
          ${isActive
            ? (isDarkMode ? 'opacity-100 scale-100' : 'opacity-85 scale-100')
            : (isDarkMode
                ? 'opacity-0 scale-95 group-hover/iconbtn:opacity-100 group-hover/iconbtn:scale-100'
                : 'opacity-0 scale-96 group-hover/iconbtn:opacity-85 group-hover/iconbtn:scale-102')
          }`}
        style={{
          background: isDarkMode
            ? 'rgba(31, 41, 55, 0.2)'
            : 'rgba(255, 255, 255, 0.45)',
          backdropFilter: isDarkMode ? 'blur(12px)' : 'blur(10px)',
          border: isActive
            ? `1px solid ${isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.8)'}`
            : `1px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.4)'}`,
          boxShadow: isActive
            ? (isDarkMode
                ? '0 4px 16px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.4)'
                : '0 6px 20px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.8)')
            : (isDarkMode
                ? '0 2px 8px rgba(0,0,0,0.2)'
                : '0 3px 12px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.5)')
        }}
      ></div>

      {/* Enhanced Colored Hover Accent - Optimiert für Light Mode */}
      <div
        className={`absolute inset-0 rounded-2xl transition-all duration-300 pointer-events-none
          ${isActive
            ? (isDarkMode ? 'opacity-15' : 'opacity-18')
            : 'opacity-0 group-hover/iconbtn:opacity-10'
          }`}
        style={{
          background: variantStyle.hoverBg.replace('bg-', '').replace('/10', '').replace('/15', '')
        }}
      ></div>
      
      {/* Icon */}
      {Icon && (
        <Icon className={`
          relative transition-all duration-300 z-10 group-hover/iconbtn:scale-[1.1]
          ${isActive
            ? (isDarkMode ? 'text-orange-300' : 'text-orange-500')
            : (isDarkMode ? 'text-white/70' : 'text-gray-700')
          }
          ${variantStyle.hoverIconColor} ${sizeStyle.iconSize}
        `} />
      )}
      
      {/* Children (for text content) */}
      {children && (
        <span className={`
          relative transition-all duration-300 z-10 group-hover/iconbtn:scale-[1.02]
          ${isDarkMode ? 'text-white/70' : 'text-gray-700'} 
          ${variantStyle.hoverIconColor}
        `}>
          {children}
        </span>
      )}
      
      {/* Badge */}
      {badge && (
        <div className={`
          absolute -top-1 -right-1 text-white text-xs rounded-full 
          min-w-[16px] h-[16px] flex items-center justify-center px-1 
          font-bold border border-white/30 backdrop-blur-sm z-20
          bg-gradient-to-r ${badgeColors[badgeColor]}
        `}>
          {badge > 99 ? '99+' : badge}
        </div>
      )}
    </button>
  );
};

export default LiquidGlassIconButton;